import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Search, Filter, RefreshCw, GitBranch } from 'lucide-react';
import VersionListItem, { VersionAction } from './VersionListItem';
import SmoothLoadingSpinner from './SmoothLoadingSpinner';
import type { GameVersion } from '../../types/api';

interface VersionHistoryPanelProps {
  projectId: string;
  versions: GameVersion[];
  isLoading: boolean;
  error: string | null;
  onActionSelect: (action: VersionAction, version: GameVersion) => void;
  onRefresh?: () => void;
  limit?: number;
  className?: string;
}

type SortField = 'version_number' | 'uploaded_at' | 'file_count' | 'total_size' | 'upload_status';
type SortOrder = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  order: SortOrder;
}

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  projectId: _projectId, // Marked as unused for now, might be needed for future features
  versions,
  isLoading,
  error,
  onActionSelect,
  onRefresh,
  limit,
  className = ''
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'uploaded_at', order: 'desc' });
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Sort versions
  const sortedVersions = React.useMemo(() => {
    const sorted = [...versions].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.field) {
        case 'version_number':
          aValue = a.version_number;
          bValue = b.version_number;
          break;
        case 'uploaded_at':
          aValue = new Date(a.uploaded_at).getTime();
          bValue = new Date(b.uploaded_at).getTime();
          break;
        case 'file_count':
          aValue = a.file_count;
          bValue = b.file_count;
          break;
        case 'total_size':
          aValue = parseInt(a.total_size) || 0;
          bValue = parseInt(b.total_size) || 0;
          break;
        case 'upload_status':
          aValue = a.upload_status;
          bValue = b.upload_status;
          break;
        default:
          aValue = a.uploaded_at;
          bValue = b.uploaded_at;
      }

      if (aValue < bValue) return sortConfig.order === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.order === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [versions, sortConfig]);

  // Filter versions
  const filteredVersions = React.useMemo(() => {
    let filtered = sortedVersions;

    // Text filter
    if (filterText) {
      filtered = filtered.filter(version =>
        version.version_number.toLowerCase().includes(filterText.toLowerCase()) ||
        version.bundle_version.toString().includes(filterText) ||
        (version.changelog && version.changelog.toLowerCase().includes(filterText.toLowerCase())) ||
        (version.upload_notes && version.upload_notes.toLowerCase().includes(filterText.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'current') {
        filtered = filtered.filter(version => version.is_current);
      } else if (statusFilter === 'archived') {
        filtered = filtered.filter(version => version.is_archived);
      } else {
        filtered = filtered.filter(version => version.upload_status === statusFilter);
      }
    }

    return filtered;
  }, [sortedVersions, filterText, statusFilter]);

  // Apply limit if specified
  const displayedVersions = limit ? filteredVersions.slice(0, limit) : filteredVersions;

  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) return null;
    return sortConfig.order === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  const getStatusOptions = () => {
    const statuses = ['all', 'current', 'completed', 'uploading', 'failed', 'partial', 'archived'];
    return statuses.map(status => ({
      value: status,
      label: status.charAt(0).toUpperCase() + status.slice(1)
    }));
  };

  if (error) {
    return (
      <div className={`card p-6 ${className}`}>
        <div className="text-center py-8">
          <div className="text-red-400 mb-4">
            <GitBranch className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2">
            Failed to load versions
          </h3>
          <p className="text-gray-500 dark:text-dark-text-muted mb-4">
            {error}
          </p>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="btn btn-primary"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`card p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
            Version History
          </h3>
          <span className="text-sm text-gray-500 dark:text-dark-text-muted">
            {filteredVersions.length} version{filteredVersions.length !== 1 ? 's' : ''}
            {limit && filteredVersions.length > limit && ` (showing ${limit})`}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn btn-secondary btn-sm ${showFilters ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="btn btn-secondary btn-sm"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Search versions..."
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {getStatusOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <SmoothLoadingSpinner 
          isLoading={isLoading} 
          size="lg" 
          message="Loading versions..." 
          className="py-12" 
        />
      )}

      {/* Empty State */}
      {!isLoading && displayedVersions.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <GitBranch className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2">
            No versions found
          </h3>
          <p className="text-gray-500 dark:text-dark-text-muted">
            {filterText || statusFilter !== 'all' 
              ? 'No versions match your current filters.' 
              : 'No versions have been uploaded yet.'}
          </p>
        </div>
      )}

      {/* Table */}
      {!isLoading && displayedVersions.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => handleSort('version_number')}
                >
                  <div className="flex items-center gap-1">
                    Version
                    {getSortIcon('version_number')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => handleSort('upload_status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {getSortIcon('upload_status')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => handleSort('total_size')}
                >
                  <div className="flex items-center gap-1">
                    Size
                    {getSortIcon('total_size')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => handleSort('file_count')}
                >
                  <div className="flex items-center gap-1">
                    Files
                    {getSortIcon('file_count')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  onClick={() => handleSort('uploaded_at')}
                >
                  <div className="flex items-center gap-1">
                    Uploaded
                    {getSortIcon('uploaded_at')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Changelog
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedVersions.map((version) => (
                <VersionListItem
                  key={version.id}
                  version={version}
                  onActionSelect={onActionSelect}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Show More Button */}
      {limit && filteredVersions.length > limit && (
        <div className="text-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => {
              // This would need to be handled by parent component
              // For now, just show a message
              console.log('Show more versions requested');
            }}
            className="btn btn-secondary"
          >
            Show All {filteredVersions.length} Versions
          </button>
        </div>
      )}
    </div>
  );
};

export default VersionHistoryPanel; 